import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  ChevronDown,
  ChevronUp,
  CheckCircle,
  FileText,
  X,
  Calendar,
  Download,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  AlertTriangle,
} from "lucide-react";
import MainPageWrapper from "../../../../../../components/MainPageWrapper/MainPageWrapper";
import { useCampaignBillingBatches } from "../../hooks/useCampaignBillingBatches";
import { useWeekSummary } from "../../hooks/useWeekSummary";
import { useInvoicePreview } from "../../hooks/useInvoicePreview";
import { useBatchStatuses } from "../../hooks/useBatchStatuses";
import { useAccountBillability } from "../../hooks/useAccountBillability";
import { CampaignBillingBatch } from "@/api/fetchCampaignBillingBatches/types";
import {
  fetchCampaignBillingBatches,
  generateInvoices,
} from "@/api/fetchCampaignBillingBatches/fetchCampaignBillingBatchesApi";
import { getISOWeekRange, getISOWeekInfo } from "@/utils/dateUtils";
import { downloadFile } from "@/utils/downloadFile";
import { useToast } from "@/components/ui/use-toast";

const TERMS_LABELS: Record<string, string> = {
  CREDIT_CARD: "Credit Card",
  ACH_TRANSFER: "ACH",
};

const formatTermsLabel = (terms: string | null): string =>
  terms ? (TERMS_LABELS[terms] ?? terms) : "No Payment Method";

const CampaignBillingList: React.FC = () => {
  const [expandedBatches, setExpandedBatches] = useState(new Set<number>());
  const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedBatchStatusId, setSelectedBatchStatusId] = useState<
    number | null
  >(null);
  const [showBatchInvoiceModal, setShowBatchInvoiceModal] = useState(false);
  const [selectedBatchesForInvoice, setSelectedBatchesForInvoice] = useState(
    new Set<number>(),
  );
  const [showWeekSelector, setShowWeekSelector] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [tempInvoiceDate, setTempInvoiceDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [dueDate, setDueDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split("T")[0];
  });
  const [showDueDateModal, setShowDueDateModal] = useState(false);
  const [tempDueDate, setTempDueDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split("T")[0];
  });
  const [referenceDate, setReferenceDate] = useState(new Date());
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState<"left" | "right">(
    "right",
  );
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(false);
  const selectedWeekRef = useRef<HTMLDivElement>(null);
  const prevOrderedWeeksRef = useRef<Array<{ week: number; year: number }>>([]);

  // Use toast hook for notifications
  const { toast } = useToast();

  // Use custom hooks for API data
  const {
    batches,
    totalCount,
    totalActualQuantity,
    loading: batchesLoading,
    filters,
    pagination,
    handleFilterChange,
    handlePaginationChange,
    refetch,
  } = useCampaignBillingBatches();

  // Generate week and year numbers to fetch (3 weeks before selected, selected week in 4th position, 3 weeks after)
  // Handles year boundaries correctly (e.g., week 51, 52, 1 across new year)
  const { yearNumbers, weeks: orderedWeeks } = useMemo(() => {
    // Selected week should be in 4th position: [week-3, week-2, week-1, SELECTED, week+1, week+2, week+3]
    return getISOWeekRange(3, 3, referenceDate);
  }, [referenceDate]);

  const {
    summaries: weekSummaries,
    currentWeek,
    currentYear,
    loading: weekSummaryLoading,
  } = useWeekSummary({
    yearNumbers,
  });

  const {
    fetchPreview,
    getPreview,
    isLoading: isInvoiceLoading,
  } = useInvoicePreview();

  const { statuses: batchStatuses, loading: batchStatusesLoading } =
    useBatchStatuses();

  // Initialize selected weeks from current week
  useEffect(() => {
    if (currentWeek && selectedWeeks.length === 0) {
      const year = currentYear || new Date().getFullYear();
      setSelectedWeeks([currentWeek]);
      setSelectedYear(year);
      handleFilterChange({ weeks: [currentWeek], year });
    }
  }, [currentWeek, selectedWeeks.length, currentYear, handleFilterChange]);

  // Update filters when weeks or batch status change
  useEffect(() => {
    if (selectedWeeks.length > 0 && selectedYear) {
      handleFilterChange({
        weeks: selectedWeeks,
        year: selectedYear,
        batchStatusId: selectedBatchStatusId || undefined,
      });
    }
  }, [selectedWeeks, selectedYear, selectedBatchStatusId, handleFilterChange]);

  // Clear batch selection when weeks change
  useEffect(() => {
    setSelectedBatchesForInvoice(new Set());
  }, [selectedWeeks, currentYear]);

  // Scroll to selected week when dropdown opens
  useEffect(() => {
    if (showWeekSelector && selectedWeekRef.current) {
      selectedWeekRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [showWeekSelector]);

  // Trigger transition animation when weeks change
  useEffect(() => {
    const prevWeeks = prevOrderedWeeksRef.current;
    const currentWeeks = orderedWeeks;

    // Check if weeks have actually changed (not just initial render)
    if (
      prevWeeks.length > 0 &&
      JSON.stringify(prevWeeks) !== JSON.stringify(currentWeeks)
    ) {
      // Determine slide direction based on which week we're moving to
      // Compare the middle week (index 3) of the old and new ordered weeks
      const prevMiddleWeek = prevWeeks[3];
      const currentMiddleWeek = currentWeeks[3];

      if (prevMiddleWeek && currentMiddleWeek) {
        // If we're moving to a later date, slide left (content comes from right)
        // If we're moving to an earlier date, slide right (content comes from left)
        const prevDate = new Date(
          prevMiddleWeek.year,
          0,
          1 + (prevMiddleWeek.week - 1) * 7,
        );
        const currentDate = new Date(
          currentMiddleWeek.year,
          0,
          1 + (currentMiddleWeek.week - 1) * 7,
        );
        setSlideDirection(currentDate > prevDate ? "left" : "right");
      }

      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 300); // Match this with CSS transition duration

      return () => clearTimeout(timer);
    }

    // Update ref for next comparison
    prevOrderedWeeksRef.current = currentWeeks;
  }, [orderedWeeks]);

  // No grouping - display batches directly
  const batchList = useMemo(() => batches, [batches]);

  const accountIds = useMemo(
    () =>
      batches
        .map((b) => b.intacctId)
        .filter((id, index, arr) => arr.indexOf(id) === index),
    [batches],
  );

  const { isBillable, getBillabilityMessage } =
    useAccountBillability(accountIds);

  const toggleBatch = (batch: CampaignBillingBatch) => {
    const newExpanded = new Set(expandedBatches);
    if (newExpanded.has(batch.id)) {
      newExpanded.delete(batch.id);
    } else {
      newExpanded.add(batch.id);
      // Fetch invoice preview when expanding
      const batchRef = String(batch.id);
      if (!getPreview(batchRef)) {
        fetchPreview({
          intacctId: batch.intacctId,
          batchReference: batch.id,
        });
      }
    }
    setExpandedBatches(newExpanded);
  };

  const selectWeek = (week: number, year?: number) => {
    // Calculate a date that falls in the selected week
    // Use the provided year or find it from the week summaries or default to current year
    const targetYear =
      year ||
      weekSummaries.find((w) => w.week === week)?.year ||
      currentYear ||
      new Date().getFullYear();

    setSelectedWeeks([week]);
    setSelectedYear(targetYear);

    // Find the Thursday of that week (ISO week standard)
    const jan4 = new Date(targetYear, 0, 4);
    const weekStart = new Date(jan4);
    weekStart.setDate(jan4.getDate() - jan4.getDay() + 1 + (week - 1) * 7);

    // Set reference date to this week so the summary view centers on it
    setReferenceDate(weekStart);
    setShowWeekSelector(false);
  };

  const navigateWeeksBackward = () => {
    const newDate = new Date(referenceDate);
    newDate.setDate(newDate.getDate() - 7); // Go back 1 week
    setReferenceDate(newDate);

    // Update selected week to match the new reference date's week
    const { week, year } = getISOWeekInfo(newDate);
    setSelectedWeeks([week]);
    setSelectedYear(year);
  };

  const navigateWeeksForward = () => {
    const newDate = new Date(referenceDate);
    newDate.setDate(newDate.getDate() + 7); // Go forward 1 week
    setReferenceDate(newDate);

    // Update selected week to match the new reference date's week
    const { week, year } = getISOWeekInfo(newDate);
    setSelectedWeeks([week]);
    setSelectedYear(year);
  };

  const navigateToToday = () => {
    const today = new Date();
    setReferenceDate(today);

    // Update selected week to current week
    const { week, year } = getISOWeekInfo(today);
    setSelectedWeeks([week]);
    setSelectedYear(year);
  };

  const navigateToStartOfYear = () => {
    const currentYearValue =
      selectedYear || currentYear || new Date().getFullYear();
    const firstDayOfYear = new Date(currentYearValue, 0, 1);
    setReferenceDate(firstDayOfYear);

    // Update selected week to first week of year
    const { week, year } = getISOWeekInfo(firstDayOfYear);
    setSelectedWeeks([week]);
    setSelectedYear(year);
  };

  const navigateToEndOfYear = () => {
    const currentYearValue =
      selectedYear || currentYear || new Date().getFullYear();
    const lastDayOfYear = new Date(currentYearValue, 11, 31);
    setReferenceDate(lastDayOfYear);

    // Update selected week to last week of year
    const { week, year } = getISOWeekInfo(lastDayOfYear);
    setSelectedWeeks([week]);
    setSelectedYear(year);
  };

  const toggleBatchSelection = (batchId: number) => {
    const newSelected = new Set(selectedBatchesForInvoice);
    if (newSelected.has(batchId)) {
      newSelected.delete(batchId);
    } else {
      newSelected.add(batchId);
    }
    setSelectedBatchesForInvoice(newSelected);
  };

  const selectableBatches = useMemo(
    () =>
      batchList.filter(
        (b) =>
          b.batchStatus.name.toLowerCase() === "complete" &&
          isBillable(b.intacctId) !== false,
      ),
    [batchList, isBillable],
  );

  const selectAllBatches = () => {
    if (
      selectedBatchesForInvoice.size === selectableBatches.length &&
      selectableBatches.length > 0
    ) {
      setSelectedBatchesForInvoice(new Set());
    } else {
      setSelectedBatchesForInvoice(new Set(selectableBatches.map((b) => b.id)));
    }
  };

  const exportToCSV = async () => {
    let allBatches: CampaignBillingBatch[] = [];
    try {
      const result = await fetchCampaignBillingBatches({
        weeks:
          filters.weeks && filters.weeks.length > 0 ? filters.weeks : undefined,
        year: filters.year,
        batchStatusId: filters.batchStatusId,
        billingType: "metered",
        page: 1,
        perPage: 1000,
      });
      allBatches = result.data.batches;
    } catch {
      allBatches = batchList;
    }

    const headers = [
      "Client Name",
      "Intacct ID",
      "Job #",
      "Batch ID",
      "Week",
      "Campaign Name",
      "Status",
      "Prospect Count",
      "USPS Count",
      "Invoice Date",
    ];

    const rows: any[] = [];
    allBatches.forEach((batch: CampaignBillingBatch) => {
      rows.push([
        `"${batch.companyName}"`,
        batch.intacctId,
        batch.id,
        batch.id,
        batch.week,
        `"${batch.campaignName}"`,
        batch.batchStatus.name,
        batch.projectedQuantity,
        batch.actualQuantity,
        invoiceDate,
      ]);
    });

    const totalProspect = rows.reduce(
      (sum, row) => sum + parseInt(row[7] || 0),
      0,
    );
    const totalUSPS = rows.reduce((sum, row) => sum + parseInt(row[8] || 0), 0);

    rows.push([]);
    rows.push(["TOTALS", "", "", "", "", "", "", totalProspect, totalUSPS, ""]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    downloadFile(
      blob,
      `campaign_invoices_week_${selectedWeeks.join("_")}_${selectedYear || new Date().getFullYear()}.csv`,
    );
  };

  return (
    <MainPageWrapper title="Campaign Invoices - Metered Billing">
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 mb-6 p-6">
            {/* Week Lock Notice */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4 flex items-center">
              <CheckCircle
                className="text-green-600 dark:text-green-400 mr-3"
                size={20}
              />
              <span className="text-green-800 dark:text-green-300">
                Week{selectedWeeks.length > 1 ? "s" : ""}{" "}
                {selectedWeeks.join(", ")},{" "}
                {selectedYear || new Date().getFullYear()}
              </span>
            </div>

            {/* USPS Total */}
            <div className="flex items-center gap-6 mb-4 px-1">
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Total USPS Pieces
                </span>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {batchesLoading ? (
                    <span className="text-gray-400">--</span>
                  ) : (
                    totalActualQuantity.toLocaleString()
                  )}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Total Batches
                </span>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {batchesLoading ? (
                    <span className="text-gray-400">--</span>
                  ) : (
                    totalCount.toLocaleString()
                  )}
                </p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-4 items-center">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Batch Status
                </label>
                <select
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                  disabled={batchStatusesLoading}
                  onChange={(e) =>
                    setSelectedBatchStatusId(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  value={selectedBatchStatusId || ""}
                >
                  <option value="">All Statuses</option>
                  {batchStatuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Week
                </label>
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 min-w-[150px] text-left flex justify-between items-center"
                    onClick={() => setShowWeekSelector(!showWeekSelector)}
                  >
                    <span className="text-sm">
                      {selectedWeeks.length === 0 ? (
                        "Select week"
                      ) : (
                        <>
                          Week {selectedWeeks[0]}
                          {selectedYear && (
                            <span className="text-xs text-gray-400 ml-1">
                              ({selectedYear})
                            </span>
                          )}
                        </>
                      )}
                    </span>
                    <ChevronDown size={16} />
                  </button>
                </div>

                {/* Week Selector Dropdown */}
                {showWeekSelector && (
                  <div className="absolute z-10 mt-16 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md shadow-lg p-3 min-w-[280px]">
                    <div className="mb-2 flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Select Week
                      </span>
                      <button
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        onClick={() => setShowWeekSelector(false)}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {(() => {
                        // Sort weeks in descending order (newest first)
                        const sortedWeeks = [...weekSummaries].sort((a, b) => {
                          if (a.year !== b.year) return b.year - a.year;
                          return b.week - a.week;
                        });

                        return sortedWeeks.map((weekData) => {
                          const isSelected =
                            selectedWeeks.includes(weekData.week) &&
                            selectedYear === weekData.year;

                          return (
                            <div
                              className={`
                                flex items-center justify-between py-2 px-2 rounded cursor-pointer
                                hover:bg-gray-50 dark:hover:bg-slate-700
                                ${isSelected ? "bg-gray-100 dark:bg-slate-700 border-l-2 border-blue-500" : ""}
                              `}
                              key={`${weekData.year}-${weekData.week}`}
                              onClick={() =>
                                selectWeek(weekData.week, weekData.year)
                              }
                              ref={isSelected ? selectedWeekRef : null}
                            >
                              <div className="flex items-center gap-2">
                                <span
                                  className={`text-sm ${isSelected ? "font-semibold text-gray-900 dark:text-white" : "text-gray-900 dark:text-white"}`}
                                >
                                  Week {weekData.week}
                                </span>
                                <span className="text-xs text-gray-400">
                                  ({weekData.year})
                                </span>
                              </div>

                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">
                                  {weekData.total || 0} total
                                </span>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>

              <div className="ml-auto flex gap-2">
                <button
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  disabled={selectedBatchesForInvoice.size === 0}
                  onClick={() => {
                    setTempInvoiceDate(invoiceDate);
                    setTempDueDate(dueDate);
                    setShowDueDateModal(true);
                  }}
                >
                  <FileText size={18} />
                  Review and Send
                </button>
                <button
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center gap-2"
                  onClick={exportToCSV}
                >
                  <Download size={18} />
                  Export CSV
                </button>
              </div>
            </div>
          </div>

          {/* Week Status Summary Bar */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 mb-4 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 dark:text-gray-300">
                Invoice Status by Week
              </h3>
              <div className="flex items-center gap-2">
                <button
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  onClick={navigateToStartOfYear}
                  title="Go to start of year"
                >
                  <ChevronsLeft size={20} />
                </button>
                <button
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  onClick={navigateWeeksBackward}
                  title="Previous week"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
                  onClick={navigateToToday}
                  title="Go to current week"
                >
                  Today
                </button>
                <button
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  onClick={navigateWeeksForward}
                  title="Next week"
                >
                  <ChevronRight size={20} />
                </button>
                <button
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  onClick={navigateToEndOfYear}
                  title="Go to end of year"
                >
                  <ChevronsRight size={20} />
                </button>
              </div>
            </div>
            {weekSummaryLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-gray-400" size={24} />
                <span className="ml-2 text-gray-500 dark:text-gray-400">
                  Loading week summaries...
                </span>
              </div>
            ) : (
              <div
                className={`grid grid-cols-7 gap-3 transition-all duration-300 ease-out ${
                  isTransitioning
                    ? `opacity-0 transform ${slideDirection === "left" ? "-translate-x-8" : "translate-x-8"}`
                    : "opacity-100 transform translate-x-0"
                }`}
              >
                {orderedWeeks.map(({ week: weekNumber, year: weekYear }) => {
                  const weekData = weekSummaries.find(
                    (w) => w.week === weekNumber && w.year === weekYear,
                  );
                  const isSelected =
                    selectedWeeks.includes(weekNumber) &&
                    selectedYear === weekYear;

                  return (
                    <div
                      className={`
                      border rounded-lg p-3 cursor-pointer transition-all
                      ${isSelected ? "border-blue-500 bg-gray-100 dark:bg-slate-700" : "border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600"}
                    `}
                      key={`${weekYear}-${weekNumber}`}
                      onClick={() => {
                        selectWeek(weekNumber, weekYear);
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            Week {weekNumber}
                          </span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {weekYear}
                          </span>
                        </div>
                      </div>

                      {!weekData ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          No data
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {Object.entries(weekData).map(([key, value]) => {
                            if (key === "week" || key === "year") return null;

                            const capitalizedKey =
                              key.charAt(0).toUpperCase() + key.slice(1);

                            return (
                              <p
                                className="text-xs text-gray-600 dark:text-gray-400"
                                key={key}
                              >
                                {capitalizedKey}: {value}
                              </p>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Batch List */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-slate-900 border-b dark:border-slate-700">
                <tr>
                  <th className="text-center px-4 py-3">
                    <input
                      checked={
                        selectableBatches.length > 0 &&
                        selectedBatchesForInvoice.size ===
                          selectableBatches.length
                      }
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={selectableBatches.length === 0}
                      onChange={selectAllBatches}
                      type="checkbox"
                    />
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Batch ID
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Company Name
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Campaign Name
                  </th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Week
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Prospect Count
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    USPS Count
                  </th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Invoice
                  </th>
                </tr>
              </thead>
              <tbody>
                {batchesLoading ? (
                  <tr>
                    <td className="px-6 py-12 text-center" colSpan={9}>
                      <div className="flex items-center justify-center">
                        <Loader2
                          className="animate-spin text-gray-400 mr-2"
                          size={24}
                        />
                        <span className="text-gray-500">
                          Loading campaign batches...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : batchList.length === 0 ? (
                  <tr>
                    <td
                      className="px-6 py-12 text-center text-gray-500"
                      colSpan={9}
                    >
                      No campaign batches found for the selected criteria.
                    </td>
                  </tr>
                ) : (
                  batchList.map((batch, index) => {
                    const isExpanded = expandedBatches.has(batch.id);
                    const isBatchLoading = isInvoiceLoading(String(batch.id));

                    return (
                      <React.Fragment key={batch.id}>
                        <tr
                          className={`border-b dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 ${index % 2 === 0 ? "bg-white dark:bg-slate-800" : "bg-gray-50 dark:bg-slate-800/50"}`}
                        >
                          <td className="text-center px-4 py-4">
                            <input
                              checked={selectedBatchesForInvoice.has(batch.id)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={
                                batch.batchStatus.name.toLowerCase() !==
                                  "complete" ||
                                isBillable(batch.intacctId) === false
                              }
                              onChange={() => toggleBatchSelection(batch.id)}
                              type="checkbox"
                            />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                            <div className="flex items-center gap-2">
                              {batch.id}
                              {isBillable(batch.intacctId) === false && (
                                <span
                                  title={getBillabilityMessage(batch.intacctId)}
                                >
                                  <AlertTriangle
                                    className="text-amber-500 dark:text-amber-400 shrink-0"
                                    size={16}
                                  />
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                            {batch.companyName}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                            {batch.campaignName}
                          </td>
                          <td className="px-6 py-4 text-sm text-center text-gray-700 dark:text-gray-300">
                            {batch.week}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                batch.batchStatus.name.toLowerCase() ===
                                "pending"
                                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                                  : batch.batchStatus.name.toLowerCase() ===
                                      "invoiced"
                                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                    : "bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300"
                              }`}
                            >
                              {batch.batchStatus.name}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">
                            {batch.projectedQuantity.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">
                            {batch.actualQuantity.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium"
                              disabled={isBatchLoading}
                              onClick={() => toggleBatch(batch)}
                            >
                              {isBatchLoading ? (
                                <>
                                  <Loader2 className="animate-spin" size={16} />
                                  Loading...
                                </>
                              ) : (
                                <>
                                  Preview Invoice
                                  {isExpanded ? (
                                    <ChevronUp size={16} />
                                  ) : (
                                    <ChevronDown size={16} />
                                  )}
                                </>
                              )}
                            </button>
                          </td>
                        </tr>

                        {/* Expanded Invoice - Show batch invoice */}
                        {isExpanded && (
                          <tr>
                            <td
                              className="bg-gray-50 dark:bg-slate-900 px-6 py-4"
                              colSpan={9}
                            >
                              {(() => {
                                const invoicePreview = getPreview(
                                  String(batch.id),
                                );

                                if (!invoicePreview) {
                                  return (
                                    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6 mb-4 text-center">
                                      <p className="text-gray-500 dark:text-gray-400">
                                        Loading invoice preview for batch{" "}
                                        {batch.id}...
                                      </p>
                                    </div>
                                  );
                                }

                                return (
                                  <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6 mb-4 last:mb-0">
                                    {/* Billability Warning */}
                                    {isBillable(batch.intacctId) === false && (
                                      <div className="flex items-start gap-3 mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                                        <AlertTriangle
                                          className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0"
                                          size={16}
                                        />
                                        <div>
                                          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                                            This account cannot be billed
                                          </p>
                                          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                                            {getBillabilityMessage(
                                              batch.intacctId,
                                            )}
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                    {/* Batch Info Header */}
                                    <div className="mb-4 pb-4 border-b border-gray-200 dark:border-slate-700">
                                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Batch {batch.id} - {batch.campaignName}
                                      </h3>
                                      <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Job #{batch.id} • Week {batch.week}
                                      </p>
                                    </div>
                                    {/* Invoice Header */}
                                    <div className="mb-6 grid grid-cols-3 gap-6">
                                      {/* Bill From */}
                                      <div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                                          Bill From
                                        </h4>
                                        <div className="text-sm text-gray-900 dark:text-white">
                                          {invoicePreview.billFrom.name && (
                                            <p className="font-medium">
                                              {invoicePreview.billFrom.name}
                                            </p>
                                          )}
                                          <p className="font-medium">
                                            {invoicePreview.billFrom.company}
                                          </p>
                                          <p>
                                            {
                                              invoicePreview.billFrom
                                                .addressLine1
                                            }
                                          </p>
                                          {invoicePreview.billFrom
                                            .addressLine2 && (
                                            <p>
                                              {
                                                invoicePreview.billFrom
                                                  .addressLine2
                                              }
                                            </p>
                                          )}
                                          <p>
                                            {invoicePreview.billFrom.city},{" "}
                                            {invoicePreview.billFrom.state}{" "}
                                            {invoicePreview.billFrom.postalCode}
                                          </p>
                                          <p className="mt-1 text-gray-600 dark:text-gray-400">
                                            {invoicePreview.billFrom.phone}
                                          </p>
                                          <p className="text-gray-600 dark:text-gray-400">
                                            {invoicePreview.billFrom.email}
                                          </p>
                                        </div>
                                      </div>

                                      {/* Bill To */}
                                      <div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                                          Bill To
                                        </h4>
                                        <div className="text-sm text-gray-900 dark:text-white">
                                          {invoicePreview.billTo.name && (
                                            <p className="font-medium">
                                              {invoicePreview.billTo.name}
                                            </p>
                                          )}
                                          <p className="font-medium">
                                            {invoicePreview.billTo.company}
                                          </p>
                                          <p>
                                            {invoicePreview.billTo.addressLine1}
                                          </p>
                                          {invoicePreview.billTo
                                            .addressLine2 && (
                                            <p>
                                              {
                                                invoicePreview.billTo
                                                  .addressLine2
                                              }
                                            </p>
                                          )}
                                          <p>
                                            {invoicePreview.billTo.city},{" "}
                                            {invoicePreview.billTo.state}{" "}
                                            {invoicePreview.billTo.postalCode}
                                          </p>
                                          <p className="mt-1 text-gray-600 dark:text-gray-400">
                                            {invoicePreview.billTo.phone}
                                          </p>
                                          <p className="text-gray-600 dark:text-gray-400">
                                            {invoicePreview.billTo.email}
                                          </p>
                                        </div>
                                      </div>

                                      {/* Invoice Info */}
                                      <div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                                          Invoice Details
                                        </h4>
                                        <div className="text-sm space-y-1 text-gray-900 dark:text-white">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">
                                              Invoice #:
                                            </span>
                                            <span className="font-medium">
                                              {invoicePreview.invoiceNumber}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">
                                              Job #:
                                            </span>
                                            <span className="font-medium">
                                              {invoicePreview.jobNumber}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">
                                              Invoice Date:
                                            </span>
                                            <span>--</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">
                                              Due Date:
                                            </span>
                                            <span>--</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">
                                              Mail Date:
                                            </span>
                                            <span>
                                              {new Date(
                                                invoicePreview.mailDate,
                                              ).toLocaleDateString()}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">
                                              Terms:
                                            </span>
                                            <span>
                                              {formatTermsLabel(
                                                invoicePreview.terms,
                                              )}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Ship To (if different from Bill To) */}
                                    {invoicePreview.shipTo.company !==
                                      invoicePreview.billTo.company && (
                                      <div className="mb-6 p-4 bg-gray-50 dark:bg-slate-900 rounded">
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                                          Ship To
                                        </h4>
                                        <div className="text-sm text-gray-900 dark:text-white">
                                          {invoicePreview.shipTo.name && (
                                            <p className="font-medium">
                                              {invoicePreview.shipTo.name}
                                            </p>
                                          )}
                                          <p className="font-medium">
                                            {invoicePreview.shipTo.company}
                                          </p>
                                          <p>
                                            {invoicePreview.shipTo.addressLine1}
                                          </p>
                                          {invoicePreview.shipTo
                                            .addressLine2 && (
                                            <p>
                                              {
                                                invoicePreview.shipTo
                                                  .addressLine2
                                              }
                                            </p>
                                          )}
                                          <p>
                                            {invoicePreview.shipTo.city},{" "}
                                            {invoicePreview.shipTo.state}{" "}
                                            {invoicePreview.shipTo.postalCode}
                                          </p>
                                        </div>
                                      </div>
                                    )}

                                    {/* Invoice Line Items */}
                                    <table className="w-full mb-6">
                                      <thead className="border-b-2 border-gray-300 dark:border-slate-600">
                                        <tr>
                                          <th className="text-left py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                            Description
                                          </th>
                                          <th className="text-right py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                            Quantity
                                          </th>
                                          <th className="text-right py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                            Unit Price
                                          </th>
                                          <th className="text-right py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                            Total
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {invoicePreview.lineItems.map(
                                          (item, idx) => (
                                            <tr
                                              className="border-b dark:border-slate-700"
                                              key={idx}
                                            >
                                              <td className="py-3 text-sm text-gray-900 dark:text-white">
                                                {item.description}
                                              </td>
                                              <td className="py-3 text-sm text-right text-gray-700 dark:text-gray-300">
                                                {item.quantity.toLocaleString()}
                                              </td>
                                              <td className="py-3 text-sm text-right text-gray-700 dark:text-gray-300">
                                                ${item.unitPrice.toFixed(4)}
                                              </td>
                                              <td className="py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                                                ${item.total.toFixed(2)}
                                              </td>
                                            </tr>
                                          ),
                                        )}
                                      </tbody>
                                    </table>

                                    {/* Invoice Totals and Summary */}
                                    <div className="grid grid-cols-2 gap-6">
                                      {/* Summary Stats */}
                                      <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4">
                                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                          Summary
                                        </h4>
                                        <div className="space-y-2 text-sm text-gray-900 dark:text-white">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">
                                              Total Pieces:
                                            </span>
                                            <span className="font-medium">
                                              {invoicePreview.totalPieces.toLocaleString()}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">
                                              Price per Piece:
                                            </span>
                                            <span className="font-medium">
                                              $
                                              {invoicePreview.pricePerPiece.toFixed(
                                                4,
                                              )}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Totals */}
                                      <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4">
                                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                          Payment Details
                                        </h4>
                                        <div className="space-y-2 text-sm text-gray-900 dark:text-white">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">
                                              Subtotal:
                                            </span>
                                            <span className="font-medium">
                                              $
                                              {invoicePreview.subTotal.toFixed(
                                                2,
                                              )}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">
                                              Tax:
                                            </span>
                                            <span className="font-medium">
                                              ${invoicePreview.tax.toFixed(2)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between pt-2 mt-2 border-t border-gray-300 dark:border-slate-600">
                                            <span className="font-semibold text-gray-900 dark:text-white">
                                              Total:
                                            </span>
                                            <span className="font-bold text-gray-900 dark:text-white">
                                              ${invoicePreview.total.toFixed(2)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between pt-2 mt-2 border-t-2 border-gray-400 dark:border-slate-500">
                                            <span className="text-base font-bold text-gray-900 dark:text-white">
                                              Balance Due:
                                            </span>
                                            <span className="text-base font-bold text-blue-600">
                                              $
                                              {invoicePreview.balanceDue.toFixed(
                                                2,
                                              )}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {totalCount > pagination.pageSize && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Page {pagination.pageIndex + 1} of{" "}
                  {Math.ceil(totalCount / pagination.pageSize)} (
                  {totalCount.toLocaleString()} total)
                </span>
                <div className="flex gap-2">
                  <button
                    className="px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
                    disabled={pagination.pageIndex === 0}
                    onClick={() =>
                      handlePaginationChange({
                        ...pagination,
                        pageIndex: pagination.pageIndex - 1,
                      })
                    }
                  >
                    Previous
                  </button>
                  <button
                    className="px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
                    disabled={
                      (pagination.pageIndex + 1) * pagination.pageSize >=
                      totalCount
                    }
                    onClick={() =>
                      handlePaginationChange({
                        ...pagination,
                        pageIndex: pagination.pageIndex + 1,
                      })
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Due Date Modal */}
          {showDueDateModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full">
                {/* Modal Header */}
                <div className="px-6 py-4 border-b dark:border-slate-700 flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Set Payment Due Date
                  </h2>
                  <button
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    onClick={() => setShowDueDateModal(false)}
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="px-6 py-6">
                  <div className="mb-6">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Please select the invoice date and payment due date for
                      these invoices.
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      The due date determines when payment must be received.
                      This is typically set based on the customer's payment
                      terms (e.g., Net 30, Net 60).
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Invoice Date
                    </label>
                    <div className="relative">
                      <input
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                        onChange={(e) => {
                          const newInvoiceDate = e.target.value;
                          setTempInvoiceDate(newInvoiceDate);
                          if (newInvoiceDate) {
                            const due = new Date(newInvoiceDate + "T00:00:00");
                            due.setDate(due.getDate() + 7);
                            const yyyy = due.getFullYear();
                            const mm = String(due.getMonth() + 1).padStart(
                              2,
                              "0",
                            );
                            const dd = String(due.getDate()).padStart(2, "0");
                            setTempDueDate(`${yyyy}-${mm}-${dd}`);
                          }
                        }}
                        type="date"
                        value={tempInvoiceDate}
                      />
                      <Calendar
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                        size={18}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Select the invoice date for all selected records
                    </p>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Payment Due Date
                    </label>
                    <div className="relative">
                      <input
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                        onChange={(e) => setTempDueDate(e.target.value)}
                        type="date"
                        value={tempDueDate}
                      />
                      <Calendar
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                        size={18}
                      />
                    </div>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Selected:{" "}
                      {tempDueDate
                        ? new Date(
                            tempDueDate + "T00:00:00",
                          ).toLocaleDateString("en-US", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : "Not selected"}
                    </p>
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-blue-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            clipRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            fillRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-800 dark:text-blue-300">
                          This will generate invoices for{" "}
                          {selectedBatchesForInvoice.size}{" "}
                          {selectedBatchesForInvoice.size === 1
                            ? "batch"
                            : "batches"}{" "}
                          from week{selectedWeeks.length > 1 ? "s" : ""}{" "}
                          {selectedWeeks.join(", ")}.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="px-6 py-4 border-t dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex justify-end gap-3">
                  <button
                    className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-700 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700 dark:text-gray-300"
                    onClick={() => setShowDueDateModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                    onClick={() => {
                      setInvoiceDate(tempInvoiceDate);
                      setDueDate(tempDueDate);
                      setShowDueDateModal(false);
                      setShowBatchInvoiceModal(true);
                    }}
                  >
                    <FileText size={16} />
                    Continue to Send Invoices
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Batch Invoice Modal */}
          {showBatchInvoiceModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Modal Header */}
                <div className="px-6 py-4 border-b dark:border-slate-700 flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Send Invoices
                  </h2>
                  <button
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    onClick={() => setShowBatchInvoiceModal(false)}
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Week{selectedWeeks.length > 1 ? "s" : ""}{" "}
                      {selectedWeeks.join(", ")},{" "}
                      {currentYear || new Date().getFullYear()} - Invoice
                      Generation Summary
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      {selectedBatchesForInvoice.size}{" "}
                      {selectedBatchesForInvoice.size === 1
                        ? "batch"
                        : "batches"}{" "}
                      selected for invoice generation
                    </p>
                  </div>

                  {/* Batch List - Only showing selected batches */}
                  <div className="border dark:border-slate-700 rounded-lg overflow-hidden mb-4">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-slate-900">
                        <tr>
                          <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                            Batch ID
                          </th>
                          <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                            Company
                          </th>
                          <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                            Campaign
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchList
                          .filter((batch) =>
                            selectedBatchesForInvoice.has(batch.id),
                          )
                          .map((batch, index) => (
                            <tr
                              className={`border-b dark:border-slate-700 ${index % 2 === 0 ? "bg-white dark:bg-slate-800" : "bg-gray-50 dark:bg-slate-800/50"}`}
                              key={batch.id}
                            >
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                {batch.id}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                                {batch.companyName}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                {batch.campaignName}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Batch Summary */}
                  {selectedBatchesForInvoice.size > 0 && (
                    <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        Batch Summary
                      </h3>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Selected Batches
                        </p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {selectedBatchesForInvoice.size}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="px-6 py-4 border-t dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedBatchesForInvoice.size > 0 && (
                        <span>
                          Ready to generate {selectedBatchesForInvoice.size}{" "}
                          invoice(s)
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-700 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700 dark:text-gray-300"
                        onClick={() => setShowBatchInvoiceModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                        disabled={
                          selectedBatchesForInvoice.size === 0 ||
                          isGeneratingInvoices
                        }
                        onClick={async () => {
                          try {
                            setIsGeneratingInvoices(true);

                            // Build the request payload
                            const batches = Array.from(
                              selectedBatchesForInvoice,
                            ).map((batchId) => ({
                              batch_id: batchId,
                              invoice_date: invoiceDate,
                              due_date: dueDate,
                            }));

                            // Send the API request
                            await generateInvoices({ batches });

                            // Show success message
                            toast({
                              title: "Success",
                              description: `Successfully generated invoices for ${selectedBatchesForInvoice.size} batches!`,
                              variant: "default",
                            });

                            // Close modal and clear selection
                            setShowBatchInvoiceModal(false);
                            setSelectedBatchesForInvoice(new Set());

                            // Refresh the batches list to show updated statuses
                            refetch();
                          } catch (error) {
                            console.error("Error generating invoices:", error);
                            toast({
                              title: "Error",
                              description:
                                error instanceof Error
                                  ? error.message
                                  : "Failed to generate invoices",
                              variant: "destructive",
                            });
                          } finally {
                            setIsGeneratingInvoices(false);
                          }
                        }}
                      >
                        {isGeneratingInvoices ? (
                          <>
                            <Loader2 className="animate-spin" size={16} />
                            Generating...
                          </>
                        ) : (
                          <>
                            <FileText size={16} />
                            Generate Invoices
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Action Bar for Selected Items */}
          {selectedBatchesForInvoice.size > 0 && !showBatchInvoiceModal && (
            <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 px-6 py-3 flex items-center gap-4 z-40">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {selectedBatchesForInvoice.size} batches selected
              </span>
              <button
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                onClick={() => setSelectedBatchesForInvoice(new Set())}
              >
                Clear Selection
              </button>
            </div>
          )}
        </div>
      </div>
    </MainPageWrapper>
  );
};

export default CampaignBillingList;
